import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { buildLegalLd } from "@/lib/page-schemas";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — DrHint" },
      { name: "description", content: "How DrHint collects, uses and protects your data. Cookies, analytics, and advertising disclosures." },
      { property: "og:url", content: "https://drhint.com/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://drhint.com/privacy" }],
    scripts: buildLegalLd({
      url: "https://drhint.com/privacy",
      name: "Privacy Policy — DrHint",
      description: "How DrHint collects, uses and protects your data: cookies, analytics, and advertising disclosures.",
      lastModified: "2026-06-01",
      trail: [
        { name: "Home", item: "https://drhint.com/" },
        { name: "Privacy", item: "https://drhint.com/privacy" },
      ],
    }),
  }),
  component: () => (
    <Layout>
      <article className="mx-auto max-w-3xl px-4 py-10 text-sm leading-relaxed">
        <h1 className="text-3xl font-bold">Privacy Policy</h1>
        <p className="mt-2 text-xs text-muted-foreground">Last updated: 1 June 2026</p>

        <p className="mt-6 text-muted-foreground">
          This privacy policy explains how DrHint ("we", "us", "our") collects, uses, and
          protects information when you visit our website. By using the site, you agree to the
          practices described below.
        </p>

        <h2 className="mt-8 text-xl font-bold">Information we collect</h2>
        <p className="mt-2 text-muted-foreground">
          We do not require you to create an account or provide personal information to view gold
          prices. When you contact us via the contact form, we collect the name, email and message
          you submit so we can reply.
        </p>
        <p className="mt-2 text-muted-foreground">
          Standard web server logs may automatically capture your IP address, browser type, pages
          visited, and the date and time of your visit. This information is used only for
          troubleshooting and aggregate analytics.
        </p>

        <h2 className="mt-8 text-xl font-bold">Cookies</h2>
        <p className="mt-2 text-muted-foreground">
          We use cookies and similar technologies to remember your preferences (such as country
          selection) and to measure aggregate site usage. You can disable cookies in your browser
          settings; some features of the site may not work correctly without them.
        </p>

        <h2 className="mt-8 text-xl font-bold">Third-party advertising</h2>
        <p className="mt-2 text-muted-foreground">
          We may display advertising provided by Google AdSense and other third-party ad networks.
          These providers may use cookies and web beacons to serve ads based on your prior visits
          to this and other websites. Google's use of advertising cookies enables it and its
          partners to serve ads to you based on your visit to our sites and/or other sites on the
          Internet.
        </p>
        <p className="mt-2 text-muted-foreground">
          You may opt out of personalised advertising by visiting{" "}
          <a href="https://www.google.com/settings/ads" className="text-primary underline" target="_blank" rel="noopener noreferrer">
            Google Ads Settings
          </a>{" "}
          or{" "}
          <a href="https://www.aboutads.info/" className="text-primary underline" target="_blank" rel="noopener noreferrer">
            aboutads.info
          </a>.
        </p>

        <h2 className="mt-8 text-xl font-bold">Analytics</h2>
        <p className="mt-2 text-muted-foreground">
          We may use analytics services to understand how visitors use our site. These services
          collect aggregate, anonymised information and do not identify individual users.
        </p>

        <h2 className="mt-8 text-xl font-bold">Data security</h2>
        <p className="mt-2 text-muted-foreground">
          We implement reasonable technical and organisational measures to protect any information
          collected. However, no method of transmission over the Internet is 100% secure.
        </p>

        <h2 className="mt-8 text-xl font-bold">Children's privacy</h2>
        <p className="mt-2 text-muted-foreground">
          Our site is not directed to children under 13. We do not knowingly collect personal
          information from children.
        </p>

        <h2 className="mt-8 text-xl font-bold">Changes to this policy</h2>
        <p className="mt-2 text-muted-foreground">
          We may update this policy occasionally. Material changes will be posted on this page with
          a revised "last updated" date.
        </p>

        <h2 className="mt-8 text-xl font-bold">Contact</h2>
        <p className="mt-2 text-muted-foreground">
          Questions about this policy? Email us at{" "}
          <a href="mailto:attapops4@gmail.com" className="text-primary underline">attapops4@gmail.com</a>.
        </p>

        <h2 className="mt-8 text-xl font-bold">Your rights under GDPR (EU/UK visitors)</h2>
        <p className="mt-2 text-muted-foreground">
          If you are in the European Economic Area or the United Kingdom, the General Data
          Protection Regulation (GDPR) gives you the right to: access the personal data we
          hold about you, request correction or deletion ("right to be forgotten"), restrict
          or object to processing, withdraw consent at any time, request data portability,
          and lodge a complaint with your local data protection authority. Our lawful bases
          for processing are your consent (for analytics and personalised ads) and our
          legitimate interest (for basic site security and aggregate analytics). To exercise
          any right, email us at the address above and we will respond within 30 days.
        </p>

        <h2 className="mt-8 text-xl font-bold">Your rights under CCPA / CPRA (California residents)</h2>
        <p className="mt-2 text-muted-foreground">
          California residents have the right to know what personal information we collect,
          to request deletion, to correct inaccurate information, and to opt out of the
          "sale" or "sharing" of personal information for cross-context behavioural
          advertising. DrHint does not sell personal information for money. We do
          allow third-party advertising partners (such as Google AdSense) to use cookies,
          which under California law can be considered "sharing". You can opt out by
          clicking "Reject" on our cookie banner, by enabling the Global Privacy Control
          (GPC) signal in your browser, or by adjusting{" "}
          <a href="https://www.google.com/settings/ads" className="text-primary underline" target="_blank" rel="noopener noreferrer">
            Google Ads Settings
          </a>. We will not discriminate against you for exercising any CCPA right.
        </p>

        <h2 className="mt-8 text-xl font-bold">Manage your cookie choice</h2>
        <p className="mt-2 text-muted-foreground">
          You can change your cookie preference at any time by clicking the button below.
          This will reopen the consent banner on your next page view.
        </p>
        <button
          type="button"
          onClick={() => {
            try {
              localStorage.removeItem("dgr-cookie-consent-v1");
              location.reload();
            } catch { /* ignore */ }
          }}
          className="mt-3 rounded-md border border-border bg-card px-3 py-2 text-xs font-semibold hover:bg-accent"
        >
          Reset cookie preferences
        </button>
      </article>
    </Layout>
  ),
});